/**
 * Read-only weekday-only month calendar for the right rail. Shows the month
 * containing the selected week (Mon–Fri columns only — no weekends), with:
 *   - past days greyed; today-forward days orderable (foreground), including
 *     trailing next-month days,
 *   - today as an outlined circle,
 *   - days with a submitted order in green,
 *   - the selected Mon–Fri week as a light band across its row.
 * Only weeks that contain a day of the displayed month are rendered. Clicking
 * a day jumps the week view to that day's week.
 */

import { cn } from '@ordo/ui/cn'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { type ReactElement, type ReactNode, useEffect, useState } from 'react'

import { dateFromKey, dateKeyFromDate, isDayOrderable, mondayOf, todayKey, weekOf } from '@/lib/school-orders/calendar'

const WEEKDAY_HEADERS = ['Mo', 'Tu', 'We', 'Th', 'Fr']

export interface WeekMonthCalendarProps {
  /** Any date inside the selected week (sets the initial displayed month and
   * re-syncs it whenever the selected week changes). */
  anchorKey: string
  /** Mon–Fri keys of the selected week (the band). Ignored when `selectedDayKey` is set. */
  selectedWeekKeys: string[]
  /**
   * Single-day selection (day-granularity views). When set, the calendar drops
   * the week band and renders the shadcn day pattern: the selected day is a
   * filled circle, today is an outlined circle (no range background).
   */
  selectedDayKey?: string
  /** Dates (`YYYY-MM-DD`) with at least one submitted order. */
  orderedDates: ReadonlySet<string>
  /**
   * Dates (`YYYY-MM-DD`) the school marked NO_SERVICE (PROD-1228).
   *
   * Rendered struck-through, muted and inert: the submit gate refuses these
   * days, so offering one as clickable invites an order that cannot be placed.
   * A day that ALREADY has a submitted order stays live — it keeps its green
   * badge and stays navigable, so a closure authored after the fact can never
   * strand the school's existing order (same rule as the family day card).
   *
   * Omitted = no closure data (a campus with no term calendar, or a cached
   * pre-flag payload) → every day renders exactly as before. Never fails
   * closed on missing data.
   */
  noServiceDates?: ReadonlySet<string>
  /** True when the order window for the week starting `mondayKey` has closed. */
  isWeekClosed?: (mondayKey: string) => boolean
  /** Jump to the week containing the clicked day (passes that week's Monday). */
  onSelectWeek?: (mondayKey: string) => void
  /**
   * Select a single day (passes the exact day key). Takes precedence over
   * `onSelectWeek` when provided — used by day-granularity views so a click
   * selects just that day instead of the whole week.
   */
  onSelectDay?: (dayKey: string) => void
  /** Optional content rendered beneath the legend, separated by a divider. */
  footer?: ReactNode
  /** Legend label for the green (ordered) dates. Defaults to the school phrasing. */
  orderedLegendLabel?: string
  /** Legend label for the black (open-window) dates. Defaults to the school phrasing. */
  openLegendLabel?: string
  /**
   * Dates (`YYYY-MM-DD`) that need attention — on Production Records, past
   * days whose record is incomplete. Rendered red: wins over the muted past
   * treatment and the green ordered colour, loses only to the selected-day
   * fill (today's ring still shows). Providing the set (even empty) adds a
   * red legend item and drops the black legend item when no day in the
   * displayed month actually renders black. Omitted = the calendar renders
   * exactly as before.
   */
  attentionDates?: ReadonlySet<string>
  /** Legend label for the red (attention) dates. */
  attentionLegendLabel?: string
}

export function WeekMonthCalendar({
  anchorKey,
  selectedWeekKeys,
  selectedDayKey,
  orderedDates,
  noServiceDates,
  isWeekClosed,
  onSelectWeek,
  onSelectDay,
  footer,
  orderedLegendLabel = 'Complete',
  openLegendLabel = 'No Orders',
  attentionDates,
  attentionLegendLabel = 'Needs attention',
}: WeekMonthCalendarProps): ReactElement {
  const clickable = Boolean(onSelectDay ?? onSelectWeek)
  // Attention mode is opt-in by the PRESENCE of the set: an empty set still
  // means "this calendar speaks that language" and shows the red legend item.
  const attentionMode = attentionDates != null
  // The displayed month is independent of the selected week so it can be paged
  // with the prev/next controls, but it re-syncs to the selected week whenever
  // that changes (e.g. the week stepper or clicking a day in another month).
  const [view, setView] = useState(() => {
    const d = dateFromKey(anchorKey)
    return { year: d.getUTCFullYear(), month: d.getUTCMonth() }
  })
  useEffect(() => {
    const d = dateFromKey(anchorKey)
    setView({ year: d.getUTCFullYear(), month: d.getUTCMonth() })
  }, [anchorKey])
  const shiftMonth = (delta: number): void =>
    setView((v) => {
      const d = new Date(Date.UTC(v.year, v.month + delta, 1))
      return { year: d.getUTCFullYear(), month: d.getUTCMonth() }
    })

  const display = new Date(Date.UTC(view.year, view.month, 1))
  const year = view.year
  const month = view.month
  const today = todayKey()
  const selected = new Set(selectedWeekKeys)
  const firstSelected = selectedWeekKeys[0]
  const lastSelected = selectedWeekKeys[selectedWeekKeys.length - 1]

  const monthLabel = display.toLocaleDateString(undefined, { month: 'long', year: 'numeric', timeZone: 'UTC' })

  // 6-week grid starting on the Sunday on/before the 1st, then drop weekends so
  // only Mon–Fri remain (document order → chunks of 5 = rows). Keep only weeks
  // that contain a day of the displayed month, so we never render a row that is
  // entirely the previous or next month.
  const firstOfMonth = new Date(Date.UTC(year, month, 1))
  const gridStart = new Date(firstOfMonth)
  gridStart.setUTCDate(1 - firstOfMonth.getUTCDay())
  const weekdayCells = Array.from({ length: 42 }, (_, i) => {
    const d = new Date(gridStart)
    d.setUTCDate(gridStart.getUTCDate() + i)
    return { key: dateKeyFromDate(d), day: d.getUTCDate(), weekday: d.getUTCDay(), inMonth: d.getUTCMonth() === month }
  }).filter((c) => c.weekday >= 1 && c.weekday <= 5)
  const cells = Array.from({ length: Math.ceil(weekdayCells.length / 5) }, (_, r) =>
    weekdayCells.slice(r * 5, r * 5 + 5),
  )
    .filter((row) => row.some((c) => c.inMonth))
    .flat()

  const dayMode = selectedDayKey != null
  const dayCells = cells.map((cell) => {
    const isToday = cell.key === today
    const isSelectedDay = dayMode && cell.key === selectedDayKey
    // In day mode there's no week band — a single day is the selection.
    const inWeek = !dayMode && selected.has(cell.key)
    const isOrdered = orderedDates.has(cell.key)
    // A no-service day with an order already on it keeps its live
    // treatment — the school must still be able to reach that order.
    const isNoService = (noServiceDates?.has(cell.key) ?? false) && !isOrdered
    // Colour by orderability, not by month: a today-forward day is
    // orderable (foreground) only while its week's order window is still
    // open — a future weekday in an already-closed week (or any past day)
    // is muted. Trailing next-month orderable days stay foreground. A
    // school-closed day is never orderable whatever the window says.
    const orderable = !isNoService && isDayOrderable(cell.key, today, isWeekClosed?.(mondayOf(cell.key)) ?? false)
    const isAttention = (attentionDates?.has(cell.key) ?? false) && !isSelectedDay
    return { ...cell, isToday, isSelectedDay, inWeek, isOrdered, isNoService, orderable, isAttention }
  })
  // The black legend item earns its place only when some displayed day
  // renders black. Outside attention mode it always shows, as it always has.
  const showOpenLegend =
    !attentionMode || dayCells.some((c) => c.orderable && !c.isOrdered && !c.isAttention && !c.isSelectedDay)

  return (
    <div className="rounded-xl border bg-card p-3">
      <div className="mb-2 flex items-center justify-between gap-1">
        <button
          type="button"
          aria-label="Previous month"
          onClick={() => shiftMonth(-1)}
          className="grid h-7 w-7 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus:outline-none focus-visible:ring-1 focus-visible:ring-ring">
          <ChevronLeft className="h-4 w-4" strokeWidth={1.5} />
        </button>
        <div className="text-sm font-semibold text-foreground">{monthLabel}</div>
        <button
          type="button"
          aria-label="Next month"
          onClick={() => shiftMonth(1)}
          className="grid h-7 w-7 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus:outline-none focus-visible:ring-1 focus-visible:ring-ring">
          <ChevronRight className="h-4 w-4" strokeWidth={1.5} />
        </button>
      </div>
      <div className="grid grid-cols-5 text-center">
        {WEEKDAY_HEADERS.map((h) => (
          <div key={h} className="py-1 text-xs font-medium text-muted-foreground">
            {h}
          </div>
        ))}
        {dayCells.map((cell) => {
          const { isToday, isSelectedDay, inWeek, isOrdered, isNoService, orderable, isAttention } = cell
          return (
            <div
              key={cell.key}
              className={cn(
                'py-0.5',
                inWeek && 'bg-muted',
                inWeek && cell.key === firstSelected && 'rounded-l-full',
                inWeek && cell.key === lastSelected && 'rounded-r-full',
              )}>
              <button
                type="button"
                disabled={!clickable || isNoService}
                aria-label={isNoService ? `${cell.day} — no service` : undefined}
                title={isNoService ? 'No service — the school is closed this day' : undefined}
                onClick={() => (onSelectDay ? onSelectDay(cell.key) : onSelectWeek?.(weekOf(cell.key)[0]!))}
                className={cn(
                  'mx-auto grid h-8 w-8 place-items-center rounded-full text-sm tabular-nums transition-colors',
                  // Past days → muted (default).
                  'text-muted-foreground/40',
                  // School-closed → struck through, never clickable. Set before
                  // the state rules below so an ordered/today ring still shows.
                  isNoService && 'line-through decoration-muted-foreground/60',
                  // Order window open (today-forward) → black.
                  !isSelectedDay && orderable && !isOrdered && 'font-medium text-foreground',
                  // Submitted order → green (wins over orderable; applies to today too).
                  !isSelectedDay && isOrdered && 'font-semibold text-emerald-600 dark:text-emerald-400',
                  // Today → outlined circle (shadcn pattern; used in every mode). Keep the
                  // ring always; only paint the number black when today has no order — an
                  // ordered today stays green (from the rule above).
                  isToday && !isSelectedDay && 'font-semibold ring-1 ring-foreground',
                  isToday && !isSelectedDay && !isOrdered && !isNoService && !isAttention && 'text-foreground',
                  // Needs attention → red (wins over muted, orderable and ordered; the
                  // today ring stays).
                  isAttention && 'font-semibold text-red-600 dark:text-red-400',
                  // Day-mode selection → filled dark circle (shadcn pattern, wins over all).
                  isSelectedDay && 'bg-foreground font-semibold text-background',
                  !isToday && !isSelectedDay && clickable && !isNoService && 'hover:bg-accent',
                )}>
                {cell.day}
              </button>
            </div>
          )
        })}
      </div>
      <div className="mt-2 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full bg-emerald-500" />
          {orderedLegendLabel}
        </span>
        {attentionMode ? (
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-red-500" />
            {attentionLegendLabel}
          </span>
        ) : null}
        {showOpenLegend ? (
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-foreground" />
            {openLegendLabel}
          </span>
        ) : null}
      </div>
      {footer ? <div className="mt-2.5 border-t pt-2.5">{footer}</div> : null}
    </div>
  )
}
